import { all, call, put, select, takeLeading } from 'redux-saga/effects';

import { transformData } from './helpers';
import { resultsActions } from './slice';

import { errorHandler } from '../errorHandler';
import { notifyActions } from '../notifications/slice';
import { SagaPayload, SagaReturn } from '../types';

import { ManualResultDetail, resultsApi } from '@/api';
import { Result, ResultType } from '@/api/__generated__/webApi';
import i18n from '@/i18n/i18n';
import { envs } from '@/shared/constants/envs';

export function* resultsSaga() {
  yield all([
    takeLeading(resultsActions.requestResults, requestResults),
    takeLeading(resultsActions.requestDynamics, requestDynamics),
    takeLeading(resultsActions.requestResultDetails, requestResultDetails),
    takeLeading(resultsActions.sendResultsByEmail, sendResultsByEmail),
    takeLeading(resultsActions.setDynamics, transformDynamicData),
  ]);
}

function* transformDynamicData(): SagaReturn {
  try {
    yield put(resultsActions.setFetching({ results: true }));
    const dynamics = yield select((s) => s.results.dynamics);
    console.info('warn, transformData executed');
    const { testResults }: Awaited<ReturnType<typeof transformData>> = dynamics
      ? yield call(transformData, dynamics)
      : { testResults: [] };
    yield put(resultsActions.setTranformedData(testResults));
  } catch (e) {
    errorHandler(e, resultsActions.setCommonError);
  } finally {
    yield put(resultsActions.setFetching({ results: false }));
  }
}

function* requestResults(): SagaReturn {
  try {
    yield put(resultsActions.setFetching({ results: true }));
    const results: Awaited<ReturnType<typeof resultsApi.getResults>> =
      yield call(resultsApi.getResults, { includeStorageResults: true });
    yield put(resultsActions.setResults(results));
    yield put(resultsActions.setFilterResults(results));
  } catch (e: unknown) {
    const error = e as { status: number };
    if (error.status === 500 || !error.status) {
      yield put(
        notifyActions.showNotifications([
          {
            id: '',
            type: 'error',
            text: i18n.t('SMTH_WENT_WRONG', {
              link: envs.PATIENT_CONTACT_SUPPORT_URL,
            }),
          },
        ])
      );
    }
    errorHandler(error, resultsActions.setCommonError);
  } finally {
    yield put(resultsActions.setFetching({ results: false }));
  }
}

function* requestDynamics() {
  try {
    yield put(resultsActions.setFetching({ dynamics: true }));
    const dynamics: Awaited<ReturnType<typeof resultsApi.getDynamics>> =
      yield call(resultsApi.getDynamics);
    yield put(resultsActions.setDynamics(dynamics));
  } catch (e) {
    errorHandler(e, resultsActions.setCommonError);
  } finally {
    yield put(resultsActions.setFetching({ dynamics: false }));
  }
}

function* requestResultDetails({
  payload: { resultId, type },
}: SagaPayload<typeof resultsActions.requestResultDetails>): SagaReturn {
  try {
    yield put(resultsActions.setFetching({ details: true }));
    const details: Awaited<ReturnType<typeof resultsApi.getResultByid>> =
      yield call(resultsApi.getResultByid, { id: resultId, type });
    if (details.type === ResultType.MANUAL) {
      yield put(resultsActions.setManualDetails(details as ManualResultDetail));
    } else {
      yield put(resultsActions.setLaboratoryDetails(details as Result));
    }
  } catch (e: unknown) {
    const error = e as { status: number };
    yield put(resultsActions.setErrorStatus(error.status));
    if (error.status === 500 || !error.status) {
      yield put(
        notifyActions.showNotifications([
          {
            id: '',
            type: 'error',
            text: i18n.t('SMTH_WENT_WRONG', {
              link: envs.PATIENT_CONTACT_SUPPORT_URL,
            }),
          },
        ])
      );
    }
    errorHandler(error, resultsActions.setCommonError);
  } finally {
    yield put(resultsActions.setFetching({ dynamics: false, details: false }));
  }
}

function* sendResultsByEmail({
  payload,
}: SagaPayload<typeof resultsActions.sendResultsByEmail>): SagaReturn {
  try {
    yield call(resultsApi.sendResultsByEmail, {
      params: { id: payload.resultId, type: payload.type },
      data: { email: payload.email },
    });
    yield put(resultsActions.setStep({ step: 'success' }));
  } catch (e) {
    yield put(resultsActions.setStep({ step: 'error' }));
    errorHandler(e, resultsActions.setCommonError);
  }
}
